/**
 * Pure helpers for unwrapping route loader errors into a human-readable
 * description. Kept separate from the React component so the branching logic
 * (axios with/without response, Apollo network vs GraphQL errors, HTTP status
 * mapping) can be unit-tested without rendering.
 *
 * No new dependencies: axios and Apollo error shapes are duck-typed so the
 * helpers do not force consumers to install either package.
 */

export type RouteErrorDescription = {
  status: 'error' | 'warning' | 'info';
  title: string;
  subTitle: string;
  details: string | null;
};

type AxiosErrorLike = Error & {
  isAxiosError: true;
  response?: { status: number; statusText?: string; data?: unknown };
  config?: { url?: string; method?: string };
  code?: string;
};

type ApolloErrorLike = Error & {
  graphQLErrors?: ReadonlyArray<{ message?: string }>;
  networkError?: unknown;
};

type RouteErrorResponseLike = { status: number; statusText?: string; data?: unknown };

export function isAxiosError(error: unknown): error is AxiosErrorLike {
  return !!error && typeof error === 'object' && (error as { isAxiosError?: boolean }).isAxiosError === true;
}

export function isApolloError(error: unknown): error is ApolloErrorLike {
  if (!error || typeof error !== 'object') return false;
  const e = error as { graphQLErrors?: unknown; networkError?: unknown; name?: string };
  return Array.isArray(e.graphQLErrors) || e.name === 'ApolloError' || 'networkError' in e;
}

/**
 * Maps an HTTP status code to the antd `Result` status type used to colour the
 * icon. 4xx is treated as a warning (the user can usually act on it); 5xx and
 * anything else is an error.
 */
export function statusToType(status: number): 'error' | 'warning' {
  return Math.floor(status / 100) === 4 ? 'warning' : 'error';
}

/**
 * Pulls a user-facing message out of a JSON response body. Tries `message`,
 * `error`, then `detail` — the keys most APIs we talk to actually use.
 */
export function extractResponseMessage(data: unknown): string | null {
  if (typeof data === 'string') return data || null;
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    const candidate = obj.message ?? obj.error ?? obj.detail;
    if (typeof candidate === 'string' && candidate.trim()) return candidate;
  }
  return null;
}

export function formatDetails(input: Record<string, unknown>): string | null {
  const entries = Object.entries(input).filter(([, v]) => v !== undefined && v !== null && v !== '');
  if (entries.length === 0) return null;
  return entries.map(([k, v]) => `${k}: ${typeof v === 'string' ? v : safeStringify(v)}`).join('\n');
}

export function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export function describeHttp(
  status: number,
  statusText: string | undefined,
  data: unknown,
  detailsExtras: Record<string, unknown> = {}
): RouteErrorDescription {
  const family = Math.floor(status / 100);
  const message = extractResponseMessage(data) ?? statusText ?? 'Request failed';
  const details = formatDetails({ status, statusText, ...detailsExtras });

  if (family === 4) {
    return {
      status: 'warning',
      title: `${status} ${statusText || 'Request error'}`,
      subTitle: message || 'The request could not be completed.',
      details,
    };
  }

  if (family === 5) {
    return {
      status: 'error',
      title: `${status} ${statusText || 'Server error'}`,
      subTitle: message || 'The server ran into a problem. Please retry shortly.',
      details,
    };
  }

  return {
    status: 'error',
    title: `${status} ${statusText || 'Error'}`,
    subTitle: message,
    details,
  };
}

export function unwrapAxios(error: unknown): RouteErrorDescription | null {
  if (!isAxiosError(error)) return null;
  const url = error.config?.url;
  const method = error.config?.method?.toUpperCase();
  if (error.response) {
    return describeHttp(error.response.status, error.response.statusText, error.response.data, {
      method,
      url,
      data: error.response.data,
      code: error.code,
    });
  }
  return {
    status: 'error',
    title: 'Network error',
    subTitle: error.message || 'Could not reach the server. Check your connection and retry.',
    details: formatDetails({ method, url, code: error.code, message: error.message }),
  };
}

export function unwrapApollo(error: unknown): RouteErrorDescription | null {
  if (!isApolloError(error)) return null;
  // networkError typically wraps the underlying transport (often an
  // AxiosError); recurse so that path produces the same description as a
  // direct axios throw.
  if (error.networkError) return describeRouteError(error.networkError);
  const messages = (error.graphQLErrors ?? [])
    .map((e) => (e && typeof e.message === 'string' ? e.message : null))
    .filter((m): m is string => !!m);
  return {
    status: 'error',
    title: 'GraphQL error',
    subTitle: messages.join('; ') || error.message || 'The query failed.',
    details: formatDetails({ name: error.name, message: error.message, graphQLErrors: error.graphQLErrors }),
  };
}

function isRouteErrorResponseLike(error: unknown): error is RouteErrorResponseLike {
  if (!error || typeof error !== 'object') return false;
  const e = error as { status?: unknown };
  return typeof e.status === 'number';
}

/**
 * Top-level dispatch. Tries each known error shape in turn; falls back to a
 * generic description for unknown values so the UI never blanks out.
 *
 * Order matters:
 * - Apollo before Axios: an ApolloError's `networkError` is often an
 *   AxiosError, and the Apollo branch recursively delegates so that path
 *   still produces the axios description.
 * - Apollo and Axios before the Response-like check: axios v1 exposes
 *   `error.status` as a getter, which would otherwise be misclassified as a
 *   thrown Response.
 */
export function describeRouteError(error: unknown): RouteErrorDescription {
  const apollo = unwrapApollo(error);
  if (apollo) return apollo;

  const axios = unwrapAxios(error);
  if (axios) return axios;

  if (isRouteErrorResponseLike(error)) {
    return describeHttp(error.status, error.statusText, error.data, { data: error.data });
  }

  if (error instanceof Error) {
    const isNetwork = /network|fetch|failed to fetch/i.test(error.message);
    return {
      status: 'error',
      title: isNetwork ? 'Network error' : 'Something went wrong',
      subTitle: isNetwork
        ? 'Could not reach the server. Check your connection and retry.'
        : error.message || 'An unexpected error occurred.',
      details: formatDetails({ name: error.name, message: error.message, stack: error.stack }),
    };
  }

  return {
    status: 'error',
    title: 'Something went wrong',
    subTitle: 'An unexpected error occurred.',
    details: formatDetails({ raw: safeStringify(error) }),
  };
}
